import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Building2, 
  TrendingUp, 
  Settings, 
  Plus, 
  Trash2, 
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Pencil
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend,
  Area,
  AreaChart,
  ReferenceLine
} from "recharts";
import { toast } from "sonner";
import { api, formatCurrency, formatPercent } from "@/lib/api";

export default function Forecasting() {
  const [properties, setProperties] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isForecastLoading, setIsForecastLoading] = useState(false);
  
  // Forecast settings
  const [settings, setSettings] = useState({
    growth_rate: 5.0,
    rental_growth_rate: 3.0,
    expense_growth_rate: 2.5,
    interest_rate_forecast: 6.0,
    years: 40
  });
  
  // Scenario events (buy/sell)
  const [events, setEvents] = useState([]);
  const [showAddEventDialog, setShowAddEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({
    year: 3,
    event_type: "buy",
    property_value: "",
    loan_amount: "",
    weekly_rent: "",
    interest_rate: "6.0",
    loan_term_years: "30",
    property_id: "",
    sale_price: ""
  });

  // Check for forecast event from Property Analyser
  useEffect(() => {
    const storedEvent = sessionStorage.getItem('forecastEvent');
    if (storedEvent) {
      try {
        const eventData = JSON.parse(storedEvent);
        // Add the event to our events list
        setEvents(prev => [...prev, {
          year: eventData.year || 1,
          event_type: "buy",
          property_value: eventData.property_value?.toString() || "",
          loan_amount: eventData.loan_amount?.toString() || "0",
          weekly_rent: eventData.weekly_rent?.toString() || "0",
          interest_rate: eventData.interest_rate?.toString() || "6.0",
          loan_term_years: eventData.loan_term_years?.toString() || "30",
          property_id: "",
          sale_price: "",
          from_analysis: true,
          analysis_address: eventData.analysis_address || ""
        }]);
        toast.success(`Property from analysis added: ${eventData.analysis_address || 'New property'}`);
        // Clear the stored event
        sessionStorage.removeItem('forecastEvent');
      } catch (e) {
        console.error("Error parsing forecast event:", e);
      }
    }
  }, []);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await api.get("/properties");
        setProperties(response.data);
      } catch (error) {
        console.error("Error fetching properties:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProperties();
  }, []);

  const runForecast = async (useEvents = true) => {
    setIsForecastLoading(true);
    try {
      // Use scenario endpoint if we have events and useEvents is true, otherwise use simple portfolio forecast
      if (events.length > 0 && useEvents) {
        const response = await api.post("/forecast/scenario", {
          settings,
          events: events.map(e => ({
            year: e.year,
            event_type: e.event_type,
            property_value: e.event_type === "buy" ? parseFloat(e.property_value) : null,
            loan_amount: e.event_type === "buy" ? parseFloat(e.loan_amount) || 0 : null,
            weekly_rent: e.event_type === "buy" ? parseFloat(e.weekly_rent) || 0 : null,
            interest_rate: e.event_type === "buy" ? parseFloat(e.interest_rate) : null,
            loan_term_years: e.event_type === "buy" ? parseInt(e.loan_term_years) : null,
            property_id: e.event_type === "sell" ? e.property_id : null,
            sale_price: e.event_type === "sell" && e.sale_price ? parseFloat(e.sale_price) : null
          }))
        });
        setForecast(response.data);
      } else {
        const response = await api.post("/forecast/portfolio", settings);
        setForecast(response.data);
      }
    } catch (error) {
      console.error("Error running forecast:", error);
      toast.error("Failed to run forecast");
    } finally {
      setIsForecastLoading(false);
    }
  };

  // Auto-run forecast on initial load
  useEffect(() => {
    if (!isLoading) {
      runForecast();
    }
  }, [isLoading]);

  // Auto-run forecast when settings change (debounced)
  useEffect(() => {
    if (isLoading || !forecast) return; // Don't run on initial load
    
    const timeoutId = setTimeout(() => {
      runForecast();
    }, 500); // 500ms debounce
    
    return () => clearTimeout(timeoutId);
  }, [settings]);

  const addEvent = () => {
    if (newEvent.event_type === "buy" && !newEvent.property_value) {
      toast.error("Please enter a property value");
      return;
    }
    if (newEvent.event_type === "sell" && !newEvent.property_id) {
      toast.error("Please select a property to sell");
      return;
    }
    
    if (editingEvent) {
      // Update existing event
      setEvents(events.map(e => e.id === editingEvent.id ? { ...newEvent, id: editingEvent.id } : e));
      setEditingEvent(null);
    } else {
      // Add new event
      setEvents([...events, { ...newEvent, id: Date.now() }]);
    }
    
    setShowAddEventDialog(false);
    setNewEvent({
      year: 3,
      event_type: "buy",
      property_value: "",
      loan_amount: "",
      weekly_rent: "",
      interest_rate: "6.0",
      loan_term_years: "30",
      property_id: "",
      sale_price: ""
    });
  };

  const editEvent = (event) => {
    setEditingEvent(event);
    setNewEvent({
      year: event.year,
      event_type: event.event_type,
      property_value: event.property_value || "",
      loan_amount: event.loan_amount || "",
      weekly_rent: event.weekly_rent || "",
      interest_rate: event.interest_rate || "6.0",
      loan_term_years: event.loan_term_years || "30",
      property_id: event.property_id || "",
      sale_price: event.sale_price || ""
    });
    setShowAddEventDialog(true);
  };

  const removeEvent = (eventId) => {
    setEvents(events.filter(e => e.id !== eventId));
  };

  const closeEventDialog = () => {
    setShowAddEventDialog(false);
    setEditingEvent(null);
    setNewEvent({
      year: 3,
      event_type: "buy",
      property_value: "",
      loan_amount: "",
      weekly_rent: "",
      interest_rate: "6.0",
      loan_term_years: "30",
      property_id: "",
      sale_price: ""
    });
  };

  // Get event years for reference lines
  const eventYears = events.map(e => e.year);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="text-center py-12" data-testid="forecasting-empty">
        <TrendingUp className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h3 className="font-heading text-xl font-semibold text-slate-700 mb-2">
          No Properties Yet
        </h3>
        <p className="text-slate-500 mb-6">
          Add properties to forecast their growth
        </p>
        <Link to="/properties/new">
          <Button className="bg-[#547d91] hover:bg-[#456a7a] rounded-full">
            Add Property
          </Button>
        </Link>
      </div>
    );
  }

  const finalYear = forecast?.forecast?.[forecast.forecast.length - 1];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="forecasting-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900">
            Portfolio Forecasting
          </h2>
          <p className="text-slate-500 mt-1">
            Project future growth with hypothetical buy/sell scenarios
          </p>
        </div>
      </div>

      {/* Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Settings className="w-5 h-5 text-[#547d91]" />
            Forecast Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label>Growth Rate (%)</Label>
              <Input
                type="number"
                step="0.5"
                value={settings.growth_rate}
                onChange={(e) => setSettings({...settings, growth_rate: parseFloat(e.target.value)})}
                data-testid="growth-rate-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Rental Growth (%)</Label>
              <Input
                type="number"
                step="0.5"
                value={settings.rental_growth_rate}
                onChange={(e) => setSettings({...settings, rental_growth_rate: parseFloat(e.target.value)})}
                data-testid="rental-growth-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Expense Growth (%)</Label>
              <Input
                type="number"
                step="0.5"
                value={settings.expense_growth_rate}
                onChange={(e) => setSettings({...settings, expense_growth_rate: parseFloat(e.target.value)})}
                data-testid="expense-growth-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Interest Rate (%)</Label>
              <Input
                type="number"
                step="0.25"
                value={settings.interest_rate_forecast}
                onChange={(e) => setSettings({...settings, interest_rate_forecast: parseFloat(e.target.value)})}
                data-testid="interest-rate-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Years</Label>
              <Input
                type="number"
                min="1"
                max="30"
                value={settings.years}
                onChange={(e) => setSettings({...settings, years: parseInt(e.target.value)})}
                data-testid="years-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scenario Events */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#547d91]" />
              Scenario Events
            </CardTitle>
            <CardDescription>
              Add hypothetical property purchases or sales to see their impact
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            className="rounded-full"
            onClick={() => setShowAddEventDialog(true)}
            data-testid="add-event-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Event
          </Button>
        </CardHeader>
        <CardContent>
          {events.length > 0 ? (
            <div className="space-y-3">
              {events.sort((a, b) => a.year - b.year).map((event) => (
                <div 
                  key={event.id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    event.event_type === "buy" 
                      ? "bg-emerald-50 border-emerald-200" 
                      : "bg-amber-50 border-amber-200"
                  }`}
                  data-testid={`event-${event.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      event.event_type === "buy" ? "bg-emerald-100" : "bg-amber-100"
                    }`}>
                      {event.event_type === "buy" ? (
                        <ArrowUpRight className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <ArrowDownRight className="w-5 h-5 text-amber-600" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={
                          event.event_type === "buy" ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"
                        }>
                          Year {event.year}
                        </Badge>
                        <span className="font-semibold text-slate-900 capitalize">
                          {event.event_type === "buy" ? "Purchase Property" : "Sell Property"}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">
                        {event.event_type === "buy" ? (
                          <>
                            Value: {formatCurrency(parseFloat(event.property_value))} | 
                            Loan: {formatCurrency(parseFloat(event.loan_amount) || 0)} | 
                            Rent: {formatCurrency((parseFloat(event.weekly_rent) || 0) * 52)}/yr
                          </>
                        ) : (
                          <>
                            {properties.find(p => p.id === event.property_id)?.address || "Property"} 
                            {event.sale_price && ` at ${formatCurrency(parseFloat(event.sale_price))}`}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-slate-400 hover:text-[#547d91]"
                      onClick={() => editEvent(event)}
                      data-testid={`edit-event-${event.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-slate-400 hover:text-red-500"
                      onClick={() => removeEvent(event.id)}
                      data-testid={`remove-event-${event.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-500 py-6">
              No scenario events added. Click "Add Event" to model buying or selling properties in the future.
            </p>
          )}
          
          {events.length > 0 && (
            <Button 
              className="w-full mt-4 bg-[#547d91] hover:bg-[#456a7a] rounded-full"
              onClick={() => runForecast(true)}
              disabled={isForecastLoading}
              data-testid="run-forecast-btn"
            >
              {isForecastLoading ? "Calculating..." : "Apply Scenario Events"}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Forecast Results */}
      {isForecastLoading ? (
        <Skeleton className="h-96" />
      ) : forecast?.forecast?.length > 0 ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="metric-card">
              <CardContent className="p-5">
                <p className="text-sm text-slate-500 mb-1">
                  Projected Value (Year {settings.years})
                </p>
                <p className="text-2xl font-bold text-[#547d91]">
                  {formatCurrency(finalYear?.total_value || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="metric-card">
              <CardContent className="p-5">
                <p className="text-sm text-slate-500 mb-1">
                  Projected Equity (Year {settings.years})
                </p>
                <p className="text-2xl font-bold text-emerald-600">
                  {formatCurrency(finalYear?.total_equity || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="metric-card">
              <CardContent className="p-5">
                <p className="text-sm text-slate-500 mb-1">
                  Projected LVR (Year {settings.years})
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {formatPercent(finalYear?.average_lvr || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="metric-card">
              <CardContent className="p-5">
                <p className="text-sm text-slate-500 mb-1">
                  Properties (Year {settings.years})
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {finalYear?.num_properties || properties.length}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Main Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Portfolio Value & Equity Growth</CardTitle>
              {events.length > 0 && (
                <CardDescription>
                  Vertical lines indicate buy/sell events
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={forecast.forecast}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" label={{ value: 'Year', position: 'bottom', offset: -5 }} />
                    <YAxis tickFormatter={(v) => `$${(v/1000000).toFixed(1)}M`} />
                    <Tooltip 
                      formatter={(value, name) => [formatCurrency(value), name]}
                      contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                    />
                    <Legend />
                    {/* Reference lines for events */}
                    {events.map((event) => (
                      <ReferenceLine 
                        key={event.id}
                        x={event.year} 
                        stroke={event.event_type === "buy" ? "#10B981" : "#F59E0B"} 
                        strokeDasharray="5 5"
                        strokeWidth={2}
                      />
                    ))}
                    <Area 
                      type="monotone" 
                      dataKey="total_value" 
                      name="Total Value"
                      stroke="#547d91" 
                      fill="#547d91" 
                      fillOpacity={0.3}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="total_equity" 
                      name="Total Equity"
                      stroke="#10B981" 
                      fill="#10B981"
                      fillOpacity={0.3}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="total_debt" 
                      name="Total Debt"
                      stroke="#F59E0B" 
                      fill="#F59E0B"
                      fillOpacity={0.3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Cashflow Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Cashflow</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={forecast.forecast}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip 
                      formatter={(value, name) => [formatCurrency(value), name]}
                      contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                    />
                    <Legend />
                    {events.map((event) => (
                      <ReferenceLine 
                        key={`line-${event.id}`}
                        x={event.year} 
                        stroke={event.event_type === "buy" ? "#10B981" : "#F59E0B"} 
                        strokeDasharray="5 5"
                      />
                    ))}
                    <Line 
                      type="monotone" 
                      dataKey="total_annual_rental" 
                      name="Annual Rent"
                      stroke="#10B981" 
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="total_annual_expenses" 
                      name="Annual Expenses"
                      stroke="#EF4444" 
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="net_cashflow" 
                      name="Net Income"
                      stroke="#547d91" 
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card className="p-8 text-center">
          <p className="text-slate-500">Add properties to see forecast projections</p>
        </Card>
      )}

      {/* Add/Edit Event Dialog */}
      <Dialog open={showAddEventDialog} onOpenChange={closeEventDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingEvent ? "Edit Scenario Event" : "Add Scenario Event"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Event Type</Label>
              <Select 
                value={newEvent.event_type} 
                onValueChange={(value) => setNewEvent({...newEvent, event_type: value})}
              >
                <SelectTrigger data-testid="event-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="buy">Buy New Property</SelectItem>
                  <SelectItem value="sell">Sell Existing Property</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Year (1-{settings.years})</Label>
              <Input
                type="number"
                min="1"
                max={settings.years}
                value={newEvent.year}
                onChange={(e) => setNewEvent({...newEvent, year: parseInt(e.target.value)})}
                data-testid="event-year-input"
              />
            </div>
            
            {newEvent.event_type === "buy" ? (
              <>
                <div className="space-y-2">
                  <Label>Property Value *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <Input
                      type="number"
                      className="pl-7"
                      placeholder="800000"
                      value={newEvent.property_value}
                      onChange={(e) => setNewEvent({...newEvent, property_value: e.target.value})}
                      data-testid="event-value-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Loan Amount</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <Input
                      type="number"
                      className="pl-7"
                      placeholder="640000"
                      value={newEvent.loan_amount}
                      onChange={(e) => setNewEvent({...newEvent, loan_amount: e.target.value})}
                      data-testid="event-loan-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Weekly Rent</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <Input
                      type="number"
                      className="pl-7"
                      placeholder="600"
                      value={newEvent.weekly_rent}
                      onChange={(e) => setNewEvent({...newEvent, weekly_rent: e.target.value})}
                      data-testid="event-rent-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Interest Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.25"
                      value={newEvent.interest_rate}
                      onChange={(e) => setNewEvent({...newEvent, interest_rate: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Loan Term (years)</Label>
                    <Input
                      type="number"
                      value={newEvent.loan_term_years}
                      onChange={(e) => setNewEvent({...newEvent, loan_term_years: e.target.value})}
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Property to Sell *</Label>
                  <Select 
                    value={newEvent.property_id} 
                    onValueChange={(value) => setNewEvent({...newEvent, property_id: value})}
                  >
                    <SelectTrigger data-testid="event-property-select">
                      <SelectValue placeholder="Select property" />
                    </SelectTrigger>
                    <SelectContent>
                      {properties.map((property) => (
                        <SelectItem key={property.id} value={property.id}>
                          {property.address}, {property.suburb}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Sale Price (optional - defaults to projected value)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <Input
                      type="number"
                      className="pl-7"
                      placeholder="Leave blank for projected value"
                      value={newEvent.sale_price}
                      onChange={(e) => setNewEvent({...newEvent, sale_price: e.target.value})}
                      data-testid="event-sale-price-input"
                    />
                  </div>
                </div>
              </>
            )}
            
            <Button 
              className="w-full bg-[#547d91] hover:bg-[#456a7a] rounded-full"
              onClick={addEvent}
              data-testid="save-event-btn"
            >
              {editingEvent ? "Save Changes" : "Add Event"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
